import type { ReactNode } from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';

import { getLocale } from '../../i18n';
import { compact } from '../../i18n/format';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';

/** A chart described by data rather than drawn by hand: the assistant's `render_chart` spec. */
export interface ChartSpec {
  kind: 'line' | 'bar' | 'stacked_bar' | 'area' | 'pie' | 'table';
  x: string;
  series: string | null;
  y: string[];
  y2: string[];
  units: Record<string, string>;
}

/** How a category (an axis tick, a series, a slice) is named: the caller owns the vocabulary. */
export type CategoryLabel = (column: string, value: unknown) => string;

type Row = Record<string, unknown>;

const PALETTE = 5;

function color(index: number): string {
  return `var(--chart-${(index % PALETTE) + 1})`;
}

/** A date the views return (`2026-09-27`) reads as the day the reader knows it by. */
export function cellText(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [, month, day] = value.split('-');
    return `${day}/${month}`;
  }
  if (typeof value === 'number') return new Intl.NumberFormat(getLocale()).format(value);
  if (Array.isArray(value)) return value.map(cellText).join(', ');
  return String(value);
}

function numeric(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return value === null || value === undefined || Number.isNaN(parsed) ? null : parsed;
}

/** Long rows (`x`, `series`, one value) turned wide, one key per series value, so a
 *  series column draws as lines like `y` columns do. */
function shaped(
  spec: ChartSpec,
  rows: readonly Row[],
  category: CategoryLabel,
): { keys: string[]; data: Row[] } {
  if (spec.series === null) {
    const keys = [...spec.y, ...spec.y2];
    return {
      keys,
      data: rows.map((row) => ({
        ...Object.fromEntries(keys.map((key) => [key, numeric(row[key])])),
        [spec.x]: category(spec.x, row[spec.x]),
      })),
    };
  }
  const series = spec.series;
  const value = spec.y[0] ?? '';
  const keys = [...new Set(rows.map((row) => category(series, row[series])))];
  const byX = new Map<string, Row>();
  for (const row of rows) {
    const x = category(spec.x, row[spec.x]);
    byX.set(x, {
      ...byX.get(x),
      [spec.x]: x,
      [category(series, row[series])]: numeric(row[value]),
    });
  }
  return { keys, data: [...byX.values()] };
}

function label(key: string, units: Record<string, string>): string {
  const unit = units[key];
  return unit === undefined ? key : `${key} (${unit})`;
}

export function DataTable(props: { rows: readonly Row[]; columns?: readonly string[] }): ReactNode {
  const columns = props.columns ?? Object.keys(props.rows[0] ?? {});
  return (
    <div className="max-h-96 overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column}>{column}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {props.rows.map((row, index) => (
            // Rows carry no identity of their own; their order is the query's.
            <TableRow key={index}>
              {columns.map((column) => (
                <TableCell key={column} data-numeric={typeof row[column] === 'number' || undefined}>
                  {cellText(row[column])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function SpecChart(props: {
  spec: ChartSpec;
  rows: readonly Row[];
  category: CategoryLabel;
  ariaLabel: string;
}): ReactNode {
  const { spec, category } = props;
  const locale = getLocale();
  const { keys, data } = shaped(spec, props.rows, category);
  const right = new Set(spec.series === null ? spec.y2 : []);
  const config: ChartConfig = Object.fromEntries(
    keys.map((key, index) => [key, { label: label(key, spec.units), color: color(index) }]),
  );
  const tick = (value: number) => compact(value, locale);

  if (spec.kind === 'pie') {
    const value = spec.y[0] ?? '';
    // A slice with no reading is left out rather than drawn as a zero.
    const slices = props.rows
      .map((row) => ({ name: category(spec.x, row[spec.x]), value: numeric(row[value]) }))
      .filter((slice): slice is { name: string; value: number } => slice.value !== null)
      .map((slice, index) => ({ ...slice, fill: color(index) }));
    const pieConfig: ChartConfig = Object.fromEntries(
      slices.map((slice) => [slice.name, { label: slice.name, color: slice.fill }]),
    );
    return (
      <ChartContainer
        config={pieConfig}
        role="img"
        aria-label={props.ariaLabel}
        initialDimension={{ width: 600, height: 288 }}
        className="aspect-auto h-72 w-full"
      >
        <PieChart>
          <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
          <Pie data={slices} dataKey="value" nameKey="name" isAnimationActive={false}>
            {slices.map((slice) => (
              <Cell key={slice.name} fill={slice.fill} />
            ))}
          </Pie>
          <ChartLegend content={<ChartLegendContent nameKey="name" />} />
        </PieChart>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer
      config={config}
      role="img"
      aria-label={props.ariaLabel}
      initialDimension={{ width: 700, height: 288 }}
      className="aspect-auto h-72 w-full"
    >
      <ComposedChart data={data} margin={{ top: 8, left: 4, right: 4 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey={spec.x} tickLine={false} axisLine={false} minTickGap={16} />
        <YAxis
          yAxisId="left"
          orientation="left"
          tickFormatter={tick}
          tickLine={false}
          axisLine={false}
          width={44}
        />
        {right.size > 0 && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={tick}
            tickLine={false}
            axisLine={false}
            width={44}
          />
        )}
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />
        {keys.map((key, index) => {
          const axis = right.has(key) ? 'right' : 'left';
          const fill = color(index);
          // The second axis always draws as a dashed line, the dashboard's dual-axis cue.
          if (axis === 'right' || spec.kind === 'line') {
            return (
              <Line
                key={key}
                dataKey={key}
                yAxisId={axis}
                stroke={fill}
                strokeWidth={2}
                strokeDasharray={axis === 'right' ? '6 4' : undefined}
                dot={{ r: 2 }}
                connectNulls
                isAnimationActive={false}
              />
            );
          }
          if (spec.kind === 'area') {
            return (
              <Area
                key={key}
                dataKey={key}
                yAxisId={axis}
                stroke={fill}
                fill={fill}
                fillOpacity={0.2}
                connectNulls
                isAnimationActive={false}
              />
            );
          }
          return (
            <Bar
              key={key}
              dataKey={key}
              yAxisId={axis}
              fill={fill}
              stackId={spec.kind === 'stacked_bar' ? 'stack' : undefined}
              radius={spec.kind === 'stacked_bar' ? 0 : 4}
              isAnimationActive={false}
            />
          );
        })}
      </ComposedChart>
    </ChartContainer>
  );
}
